import { getConfig, klinikenApi } from "@/api";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { Anhorigbesok, Slutenvardsbesok } from "@/tabs/Vardplatsbokning/types";
import cloneDeep from "lodash/cloneDeep";

type State = BaseState & {
    slutenvardsbesok: Map<string | number, Slutenvardsbesok>;
};

type Commit = (
    action: string,
    payload: unknown,
    options?: {
        root?: boolean;
    }
) => void;

type Getters = {
    slutenvardsbesokById: (data: { id: number | string }) => Slutenvardsbesok | null;
};

const filterParamConverter = ({ filterParams }: { filterParams: FilterParams }) => {
    return Object.fromEntries(
        Object.entries(filterParams).map(([key, value]) => {
            if (key === "show_strukna") {
                return [`status${value ? "__any" : "__ne"}`, "Struken"];
            }

            return [key, value];
        })
    );
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        slutenvardsbesok: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "slutenvardsbesok";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        /**
         * Den här ska inte anropas direkt. Hämtning ska göras via actionen (fetchSlutenvardsbesok)
         */
        slutenvardsbesok: (state: State) => [...state.slutenvardsbesok.values()],
        /**
         * Anropar en generisk getter för hämtning av subset. computed i en komponent som lyssnar på denna getter
         * är förmodligen vad du vill ha i din komponent.
         * Se helpers/baseSubset
         */
        slutenvardsbesokSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({
                ...props,
                state,
                collectionName: moduleName,
            }),
        /**
         * Om du bara vill hämta ett objekt vid ett enda tillfälle så ska det göras via actionen och inte här
         * (getSlutenvardsbesokById, som hämtar från BE om objektet inte finns i store).
         * Om du vill lyssna på förändringar på ett enskilt objekt så vill du troligen lyssna på den här i en
         * computed i din komponent
         */
        slutenvardsbesokById:
            (state: State) =>
            ({ id }: { id?: number | string }) => {
                if (!id) {
                    return null;
                }

                return state.slutenvardsbesok.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        /**
         * Tillägg av nya objekt i store anropar i sin tur en generisk setter. Den här ska inte anropas direkt utan
         * istället gå via actionen (createSlutenvardsbesok)
         * Se helpers/subset
         */
        addSlutenvardsbesok: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                slutenvardsbesok = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                slutenvardsbesok: Slutenvardsbesok[];
            }
        ) =>
            subsetSetter<State, Slutenvardsbesok>({
                offset,
                subsetName,
                state,
                resetSubset,
                filterParamConverter,
                collectionName: moduleName,
                items: slutenvardsbesok,
            }),
        /**
         * Uppdatering av objekt i store anropar i sin tur en generisk setter. Den här ska inte anropas direkt utan
         * istället gå via actionen (updateSlutenvardsbesok)
         * Se helpers/subset
         */
        updateSlutenvardsbesok: (
            state: State,
            {
                slutenvardsbesok,
                offset,
                subsetName,
            }: {
                slutenvardsbesok: Slutenvardsbesok[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, Slutenvardsbesok>({
                offset,
                state,
                filterParamConverter,
                collectionName: moduleName,
                items: slutenvardsbesok,
            }),
    },
    /**
     * Actions ska alltid innehålla baseActions + det som är specifikt för modulen. Den returnerar resultatet,
     * men det du troligen vill göra är att lyssna på
     */
    actions: {
        ...baseActions({ moduleName }),
        getSlutenvardsbesokById: async (
            {
                getters,
                commit,
                state,
            }: BaseActionsProps<State> & {
                getters: Getters;
            },
            {
                id,
                addAsNew,
                addAsUpdate,
                config = {},
            }: {
                id: number | string;
                // Ska hämtade datan stoppas in i newlyCreated subset?
                addAsNew?: boolean;
                /*
                    Ska hämtningen läggas till som newlyUpdated?
                    Existerande data kommer att ignoreras i store och en ny hämtning från BE kommer att göras
                 */
                addAsUpdate?: boolean;
                config?: {
                    tabbnamn?: string;
                };
            }
        ) => {
            const foundBesok = getters.slutenvardsbesokById({ id });

            if (!addAsUpdate && (foundBesok || !id)) {
                if (foundBesok && addAsNew) {
                    commit("addSlutenvardsbesok", {
                        offset: 0,
                        slutenvardsbesok: [foundBesok],
                        subsetName: "newlyCreated",
                    });
                }

                return foundBesok;
            }

            try {
                const { data: retrievedBesok } = await klinikenApi.get(
                    `slutenvard/slutenvardsbesok/${id}/`,
                    getConfig(config)
                );

                if (retrievedBesok) {
                    commit("addSlutenvardsbesok", {
                        offset: addAsNew ? Object.keys(state.subsets.newlyCreated.ids).length : 0,
                        slutenvardsbesok: [retrievedBesok],
                        subsetName: addAsNew ? "newlyCreated" : id,
                    });

                    if (addAsUpdate) {
                        commit("updateSlutenvardsbesok", {
                            subsetName: "newlyUpdated",
                            slutenvardsbesok: [retrievedBesok],
                            offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                        });
                    }

                    return retrievedBesok;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        /**
         * Hämta slutenvårdsbesök från state eller från backend och stoppa in i state. Den returnerar inte resultatet.
         * Det du troligen vill göra är att skapa en computed som lyssnar på subsetgettern.
         * Den kapar ett nytt subset om inskickade filteringParams inte redan finns i state.
         * @param limit mängden objekt du vill få tillbaka i resultatet
         * @param page nummer för page. Används för att räkna ut offset som skickas till BE
         * @param filterParams alla sök och filtreringsparametrar. "ordering" är för sortering
         * @param forceRefresh Ska en hämtning från BE göras, oberoende av om det redan finns i store?
         * @param resetSubset Ska hela subsetet rensas innan ny data sätts?
         * @param resetNewlyCreated Ska subset med nyligen skapade rensas? Kan användas i t.ex listor där användaren
         * gör en ny filtrering eller manuell refresh och vi inte vill att nyligen skapade ska fortsätta visas
         */
        fetchSlutenvardsbesok: async (
            { commit, state }: BaseActionsProps<State>,
            {
                filterParams,
                forceRefresh,
                resetSubset,
                resetNewlyCreated,
                limit = 20,
                page = 1,
                config = {},
            }: {
                limit: number;
                page: number;
                filterParams: FilterParams;
                forceRefresh?: boolean;
                resetSubset?: boolean;
                /*
                    Ska subset med nyligen skapade rensas? Kan användas i t.ex listor där användaren
                    gör en ny filtrering eller manuell refresh och vi inte vill att nyligen skapade ska fortsätta visas
                 */
                resetNewlyCreated?: boolean;
                config?: {
                    tabbnamn?: string;
                };
            }
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Slutenvardsbesok>({
                    filterParams,
                    state,
                    limit,
                    page,
                    forceRefresh,
                    collectionName: moduleName,
                    apiUrl: "slutenvard/slutenvardsbesok/",
                    config,
                });

                if (resetNewlyCreated) {
                    commit("addSlutenvardsbesok", {
                        offset: 0,
                        subsetName: "newlyCreated",
                        resetSubset: true,
                        slutenvardsbesok: [],
                    });
                }

                if (!results) {
                    return {
                        // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                        subsetName, // Totala mängden träffar från sökningen
                        total, // Nummer för nästa page
                        nextPage,
                    };
                }

                commit("addSlutenvardsbesok", {
                    offset,
                    subsetName,
                    resetSubset,
                    slutenvardsbesok: results,
                });

                return {
                    // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                    subsetName, // Totala mängden träffar från sökningen
                    total, // Nummer för nästa page
                    nextPage,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        partialUpdateSlutenvardsbesok: async (
            { commit, state, dispatch }: BaseActionsProps<State>,
            {
                slutenvardsbesok,
            }: {
                slutenvardsbesok: Partial<Slutenvardsbesok>;
            }
        ) => {
            try {
                const response = await klinikenApi.patch(
                    `slutenvard/slutenvardsbesok/${slutenvardsbesok.id}/`,
                    slutenvardsbesok,
                    getConfig({})
                );

                const { data: updatedBesok } = await klinikenApi.get(
                    `slutenvard/slutenvardsbesok/${slutenvardsbesok.id}/`,
                    getConfig({})
                );

                commit("updateSlutenvardsbesok", {
                    subsetName: "newlyUpdated",
                    slutenvardsbesok: [updatedBesok],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });

                // Hämta senaste vårdplatsbeställning kopplat till besök, då det kan ha ändrats i BE
                if (response.data?.vardplatsbestallning) {
                    await dispatch(
                        "vardplatsbestallning/getVardplatsbestallningById",
                        {
                            id: response.data.vardplatsbestallning,
                            addAsUpdate: true,
                        },
                        { root: true }
                    );
                }
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        partialUpdateAnhorigbesok: async (
            { commit, state, dispatch }: BaseActionsProps<State>,
            {
                anhorigbesok,
            }: {
                anhorigbesok: Partial<Anhorigbesok>;
            }
        ) => {
            try {
                const { data: updatedAnhorigbesok } = await klinikenApi.patch(
                    `slutenvard/anhorigbesok/${anhorigbesok.id}/`,
                    anhorigbesok,
                    getConfig({})
                );

                const { data: slutenvardsbesok } = await klinikenApi.get(
                    `slutenvard/slutenvardsbesok/${updatedAnhorigbesok.slutenvardsbesok}/`,
                    getConfig({})
                );

                commit("updateSlutenvardsbesok", {
                    subsetName: "newlyUpdated",
                    slutenvardsbesok: [slutenvardsbesok],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });

                // Hämta senaste vårdplatsbeställning kopplat till besök, då det kan ha ändrats i BE
                if (slutenvardsbesok.vardplatsbestallning) {
                    await dispatch(
                        "vardplatsbestallning/getVardplatsbestallningById",
                        {
                            id: slutenvardsbesok.vardplatsbestallning.id,
                            addAsUpdate: true,
                        },
                        { root: true }
                    );
                }
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
